import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, MessageCircle } from "lucide-react";
import * as Icons from "lucide-react";
import { SERVICES, COMPANY } from "@/lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/leistungen" replace />;
  const Icon = Icons[service.icon] || Icons.Wrench;
  const index = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(index + 1) % SERVICES.length];

  return (
    <div data-testid={`service-detail-${service.slug}`}>
      {/* Header banner */}
      <section className="relative pt-32 pb-28 lg:pt-40 lg:pb-32 bg-messinger-bg overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-messinger-bg via-messinger-bg/85 to-messinger-bg/40" />
        </div>
        <div className="container-x relative">
          <Link
            to="/leistungen"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-white/60 hover:text-messinger-orange transition-colors text-xs tracking-[0.18em] uppercase mb-8"
          >
            <ArrowLeft size={14} /> Alle Leistungen
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <Icon size={18} className="text-messinger-orange" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-messinger-orange">
              Leistung 0{index + 1} · 0{SERVICES.length}
            </span>
          </div>
          <h1 className="font-heading font-semibold text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] max-w-4xl text-balance">
            {service.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">
            {service.short}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-messinger-surface py-20 lg:py-24 relative overflow-hidden">
        <div className="container-x grid lg:grid-cols-12 gap-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-messinger-orange" />
              <span className="label-eyebrow">Worum es geht</span>
            </div>
            <h2 className="font-heading font-semibold text-white text-3xl sm:text-4xl leading-tight text-balance">
              Was Sie von diesem Gutachten erwarten können.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed text-lg">
              {service.intro}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative surface-card text-white p-8 rounded-2xl overflow-hidden">
              <div className="relative">
                <span className="label-eyebrow">Termin anfragen</span>
                <h3 className="mt-3 font-heading font-semibold text-2xl">
                  In 24 Stunden zur Rückmeldung.
                </h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Schildern Sie kurz Ihr Anliegen — wir stimmen Termin, Ablauf
                  und Aufwand persönlich mit Ihnen ab.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/kontakt"
                    className="cta-orange group inline-flex items-center justify-between px-5 py-3 bg-messinger-orange text-white font-semibold rounded-full text-sm"
                  >
                    Anfrage senden <ArrowRight size={16} />
                  </Link>
                  <a
                    href={COMPANY.whatsappHref}
                    className="inline-flex items-center justify-between px-5 py-3 border border-white/30 text-white hover:border-messinger-orange hover:text-messinger-orange rounded-full text-sm transition-colors"
                  >
                    WhatsApp <MessageCircle size={16} />
                  </a>
                  <a
                    href={COMPANY.phoneHref}
                    className="inline-flex items-center justify-between px-5 py-3 border border-white/30 text-white hover:border-messinger-orange hover:text-messinger-orange rounded-full text-sm transition-colors"
                  >
                    Anrufen <Phone size={16} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process steps */}
      <section className="bg-messinger-bg py-24 lg:py-28 relative">
        <div className="container-x relative">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-messinger-orange" />
              <span className="label-eyebrow">Ablauf</span>
            </div>
            <h2 className="font-heading font-semibold text-white text-3xl sm:text-4xl leading-tight">
              Vier Schritte — transparent von Anfang an.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative surface-card p-7 rounded-2xl hover:border-messinger-orange/50 transition-colors"
                data-testid={`service-step-${i}`}
              >
                <div className="font-heading text-4xl font-bold text-messinger-orange/40 leading-none">
                  0{i + 1}
                </div>
                <h3 className="mt-4 font-heading font-semibold text-white text-lg">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  {step.text}
                </p>
                {i < service.steps.length - 1 && (
                  <ArrowRight
                    size={16}
                    className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-messinger-orange"
                  />
                )}
              </motion.div>
            ))}
          </div>

          <motion.ul
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 grid sm:grid-cols-2 gap-3 max-w-3xl"
          >
            {[
              "Persönliche Begutachtung — kein Subunternehmer",
              "Beweissichere Foto- und Schriftdokumentation",
              "Verständliche Sprache, klare Ergebnisse",
              "Termine vor Ort, in der Werkstatt oder im Büro",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm text-white/80">
                <CheckCircle2 size={18} className="text-messinger-orange mt-0.5 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* Next service nav */}
      <section className="bg-messinger-surface py-16 border-t border-white/5">
        <div className="container-x flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-messinger-orange font-semibold mb-2">
              Weiter zur nächsten Leistung
            </div>
            <Link
              to={`/leistungen/${next.slug}`}
              data-testid="next-service-link"
              className="font-heading font-semibold text-white text-2xl hover:text-messinger-orange transition-colors"
            >
              {next.title}
            </Link>
          </div>
          <Link
            to={`/leistungen/${next.slug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-white hover:text-messinger-orange transition-colors"
          >
            Ansehen <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
